import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Optimized scroll handler
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Optimized cart count update
  const updateCartCount = useCallback(() => {
    try {
      const savedCart = localStorage.getItem('vybe_cart');
      if (savedCart) {
        const cart = JSON.parse(savedCart);
        const totalItems = cart.reduce((sum: number, item: any) => sum + (item.quantity || 1), 0);
        setCartCount(totalItems);
      } else {
        setCartCount(0);
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
      setCartCount(0);
    }
  }, []);

  useEffect(() => {
    updateCartCount();

    const handleCartUpdate = () => {
      updateCartCount();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('storage', handleCartUpdate);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('storage', handleCartUpdate);
    };
  }, [updateCartCount]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navigation = [
    { name: 'Accueil', href: '/' },
    { name: 'Boutique', href: '/boutique' },
    { name: 'VYBE™', href: '/vybe' },
    { name: 'QUADLUX™', href: '/quadlux' },
    { name: 'Formations', href: '/formations' },
    { name: 'Presets', href: '/presets' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-[#0A0A0A]/98 backdrop-blur-xl border-b border-white/30 shadow-lg shadow-white/10' 
        : 'bg-[#0A0A0A]/95 backdrop-blur-md border-b border-gray-800'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-2 group"
            onClick={handleLinkClick}
          >
            <div className="relative transform-gpu hover:animate-pulse-3d transition-all duration-500">
              <img 
                src="https://static.readdy.ai/image/445bd39afde876450f502e12e8cf89e5/81b291b3f4cfb9cdbfd24b12e38318c8.png" 
                alt="VYBE PARIS" 
                className="h-12 w-auto filter brightness-0 invert animate-logo-glow hover:animate-logo-hover transition-all duration-300"
                style={{
                  filter: 'brightness(0) invert(1) drop-shadow(0 0 8px rgba(255, 255, 255, 0.6)) drop-shadow(0 0 15px rgba(255, 255, 255, 0.4)) drop-shadow(0 0 25px rgba(255, 255, 255, 0.2))',
                }}
              />
              {/* Effet de particules subtiles autour du logo */}
              <div className="absolute -inset-1 opacity-20 pointer-events-none">
                <div className="absolute top-1 left-1 w-0.5 h-0.5 bg-white rounded-full animate-logo-particle-1"></div>
                <div className="absolute top-2 right-1 w-0.5 h-0.5 bg-white rounded-full animate-logo-particle-2"></div>
                <div className="absolute bottom-1 left-2 w-0.5 h-0.5 bg-white rounded-full animate-logo-particle-3"></div>
                <div className="absolute bottom-2 right-2 w-0.5 h-0.5 bg-white rounded-full animate-logo-particle-4"></div>
              </div>
              {/* Halo lumineux subtil */}
              <div className="absolute inset-0 bg-white/10 rounded-lg blur-md animate-logo-halo opacity-30 pointer-events-none"></div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-all duration-300 hover:text-white hover:scale-105 relative group ${
                  isActive(item.href) 
                    ? 'text-white animate-glow-text' 
                    : 'text-gray-300'
                }`}
                style={{
                  textShadow: isActive(item.href) ? '0 0 10px rgba(255, 255, 255, 0.8)' : 'none'
                }}
              >
                {item.name}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full animate-glow ${
                  isActive(item.href) ? 'w-full' : ''
                }`}></span>
              </Link>
            ))}
          </nav>

          {/* Cart and Mobile Menu */}
          <div className="flex items-center space-x-4">
            {/* Cart Icon */}
            <Link
              to="/cart"
              className="relative p-2 text-gray-300 hover:text-white transition-all duration-300 hover:scale-110 group"
              onClick={handleLinkClick}
            >
              <i className="ri-shopping-cart-line text-xl group-hover:animate-bounce" style={{
                filter: 'drop-shadow(0 0 5px rgba(255, 255, 255, 0.5))'
              }}></i>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-white text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse min-w-[20px]" style={{
                  boxShadow: '0 0 10px rgba(255, 255, 255, 0.8)'
                }}>
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Link>

            {/* Mobile menu button */}
            <button
              onClick={handleMenuToggle}
              className="md:hidden p-2 text-gray-300 hover:text-white transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 rounded-lg"
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
              style={{
                filter: 'drop-shadow(0 0 5px rgba(255, 255, 255, 0.5))'
              }}
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl transition-transform duration-300 ${
                isMenuOpen ? 'rotate-180' : ''
              }`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden ${
          isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-2 pt-2 pb-3 space-y-1 bg-[#0A0A0A]/98 backdrop-blur-xl border-t border-white/30 rounded-b-lg">
            {navigation.map((item, index) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={handleLinkClick}
                className={`block px-3 py-3 text-base font-medium transition-all duration-300 hover:text-white hover:bg-white/10 rounded-lg transform hover:scale-105 ${
                  isActive(item.href) 
                    ? 'text-white bg-white/20 animate-glow-text' 
                    : 'text-gray-300'
                }`}
                style={{ 
                  animationDelay: `${index * 0.1}s`,
                  animation: isMenuOpen ? 'slideInUp 0.3s ease-out forwards' : 'none',
                  textShadow: isActive(item.href) ? '0 0 10px rgba(255, 255, 255, 0.8)' : 'none'
                }}
              >
                <i className={`ri-${
                  item.href === '/' ? 'home' :
                  item.href === '/boutique' ? 'store' :
                  item.href === '/vybe' ? 'camera' :
                  item.href === '/quadlux' ? 'roadster' :
                  item.href === '/formations' ? 'graduation-cap' :
                  item.href === '/presets' ? 'image' :
                  'mail'
                }-line mr-3 text-lg`}></i>
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
